import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'
import type { TenantSettingsResponse } from '@/lib/api/controlPlaneClient'
import { SettingRow, Divider } from './SettingRow'

export function ProductDisplaySettings() {
  const {
    settings,
    isLoading,
    isError,
    updateAsync,
    isUpdating,
    queryKey,
  } = useStorefrontTenantSettings()
  const queryClient = useQueryClient()

  const [localMin, setLocalMin] = useState('')
  const [localMax, setLocalMax] = useState('')
  const [localOosUrl, setLocalOosUrl] = useState('')
  const [localOosText, setLocalOosText] = useState('')
  const [localOosColor, setLocalOosColor] = useState('')

  // Sync local min/max from server
  useEffect(() => {
    if (settings) {
      setLocalMin(String(settings.fake_viewer_min ?? 3))
      setLocalMax(String(settings.fake_viewer_max ?? 15))
      setLocalOosUrl(settings.oos_button_url ?? '')
      setLocalOosText(settings.oos_button_text ?? '')
      setLocalOosColor(settings.oos_button_color ?? '')
    }
  }, [settings?.fake_viewer_min, settings?.fake_viewer_max, settings?.oos_button_url, settings?.oos_button_text, settings?.oos_button_color])

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load display settings')
    }
  }, [isError])

  const handleToggleFakeViewers = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, show_fake_viewers: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ show_fake_viewers: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleStockQuantity = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, show_stock_quantity: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ show_stock_quantity: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleShowPrices = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, show_prices: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ show_prices: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleContactButton = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, pdp_contact_button: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ pdp_contact_button: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleOosLineChat = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, oos_line_chat: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ oos_line_chat: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleSaveOosField = async (
    field: 'oos_button_url' | 'oos_button_text' | 'oos_button_color',
    value: string,
  ) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) => (old ? { ...old, [field]: value } : old),
    )

    try {
      await updateAsync({ [field]: value })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleB2bQuotation = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, enable_b2b_quotation: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ enable_b2b_quotation: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleSaveViewerRange = async () => {
    const min = Math.max(1, parseInt(localMin, 10) || 1)
    const max = Math.max(1, parseInt(localMax, 10) || 15)
    if (min > max) {
      toast.error('Min must be less than or equal to Max.')
      return
    }

    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old ? { ...old, fake_viewer_min: min, fake_viewer_max: max } : old,
    )

    try {
      await updateAsync({ fake_viewer_min: min, fake_viewer_max: max })
      toast('Viewer range saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const showViewerRange = !!settings?.show_fake_viewers

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-0">
        {/* Show Fake Viewer Count */}
        <SettingRow
          label="Show Viewer Count"
          subtitle="Display a random 'X people viewing' count on product pages"
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.show_fake_viewers}
              onCheckedChange={handleToggleFakeViewers}
              disabled={isUpdating}
              aria-label="Show fake viewer count on product pages"
            />
          )}
        </SettingRow>

        {/* Viewer Range — only shown when fake viewers enabled */}
        {showViewerRange && (
          <>
            <Divider />
            <div className="flex items-center justify-between px-4 py-5">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">Viewer Range</span>
                <span className="text-xs text-muted-foreground">
                  Random count between min and max
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  value={localMin}
                  onChange={(e) => setLocalMin(e.target.value)}
                  onBlur={handleSaveViewerRange}
                  className="w-16 h-8 text-center text-sm"
                  disabled={isUpdating}
                  aria-label="Minimum viewer count"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="number"
                  min={1}
                  value={localMax}
                  onChange={(e) => setLocalMax(e.target.value)}
                  onBlur={handleSaveViewerRange}
                  className="w-16 h-8 text-center text-sm"
                  disabled={isUpdating}
                  aria-label="Maximum viewer count"
                />
              </div>
            </div>
          </>
        )}

        <Divider />

        {/* Show Stock Quantity */}
        <SettingRow
          label="Show Stock Quantity"
          subtitle="Display remaining stock on product pages"
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.show_stock_quantity}
              onCheckedChange={handleToggleStockQuantity}
              disabled={isUpdating}
              aria-label="Show stock quantity on product pages"
            />
          )}
        </SettingRow>

        <Divider />

        {/* Show Prices */}
        <SettingRow
          label="Show Prices"
          subtitle="Display product prices on product pages. Turn off to hide prices and show 'Contact us for pricing' instead."
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={settings?.show_prices !== 0}
              onCheckedChange={handleToggleShowPrices}
              disabled={isUpdating}
              aria-label="Show prices on product pages"
            />
          )}
        </SettingRow>

        <Divider />

        {/* Contact Us Button */}
        <SettingRow
          label="Contact Us Button"
          subtitle="Replace 'Add to Cart' with a 'Contact Us' button on product pages"
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.pdp_contact_button}
              onCheckedChange={handleToggleContactButton}
              disabled={isUpdating}
              aria-label="Show contact us button instead of add to cart"
            />
          )}
        </SettingRow>

        <Divider />

        {/* OOS LINE Chat CTA */}
        <SettingRow
          label="Out-of-Stock LINE Chat"
          subtitle="Show a 'Chat with sales' button on out-of-stock products (opens LINE with product info pre-filled)"
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.oos_line_chat}
              onCheckedChange={handleToggleOosLineChat}
              disabled={isUpdating}
              aria-label="Show LINE chat button on out-of-stock products"
            />
          )}
        </SettingRow>

        {/* OOS Button URL + Text — only shown when OOS LINE Chat is enabled */}
        {!!settings?.oos_line_chat && (
          <>
            <Divider />
            <div className="flex items-center justify-between px-4 py-5">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">Button URL</span>
                <span className="text-xs text-muted-foreground">
                  Custom link for the out-of-stock button (e.g. LINE, WhatsApp). Defaults to LINE OA if empty.
                </span>
              </div>
              <Input
                type="url"
                placeholder="https://line.me/..."
                value={localOosUrl}
                onChange={(e) => setLocalOosUrl(e.target.value)}
                onBlur={() =>
                  handleSaveOosField('oos_button_url', localOosUrl.trim())
                }
                className="w-60 h-8 text-sm"
                disabled={isUpdating}
                aria-label="Out-of-stock button URL"
              />
            </div>
            <Divider />
            <div className="flex items-center justify-between px-4 py-5">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">Button Text</span>
                <span className="text-xs text-muted-foreground">
                  Custom text for the out-of-stock button. Defaults to &ldquo;แชทกับฝ่ายขาย&rdquo; if empty.
                </span>
              </div>
              <Input
                type="text"
                placeholder="แชทกับฝ่ายขาย"
                value={localOosText}
                onChange={(e) => setLocalOosText(e.target.value)}
                onBlur={() =>
                  handleSaveOosField('oos_button_text', localOosText.trim())
                }
                className="w-60 h-8 text-sm"
                disabled={isUpdating}
                aria-label="Out-of-stock button text"
              />
            </div>
            <Divider />
            <div className="flex items-center justify-between px-4 py-5">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">Button Color</span>
                <span className="text-xs text-muted-foreground">
                  Pick a color for the out-of-stock button. Defaults to gray if empty.
                </span>
              </div>
              <div className="flex items-center gap-2">
                {[
                  { label: 'LINE', color: '#06C755' },
                  { label: 'Gray', color: '#555555' },
                  { label: 'Orange', color: '#FF6600' },
                  { label: 'Blue', color: '#0084FF' },
                ].map((preset) => (
                  <button
                    key={preset.color}
                    type="button"
                    onClick={() => {
                      setLocalOosColor(preset.color)
                      handleSaveOosField('oos_button_color', preset.color)
                    }}
                    disabled={isUpdating}
                    className={`flex h-8 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors ${
                      localOosColor.toUpperCase() === preset.color.toUpperCase()
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    <span
                      className="size-3.5 rounded-full border border-black/10"
                      style={{ backgroundColor: preset.color }}
                    />
                    {preset.label}
                  </button>
                ))}
                <div className="relative">
                  <input
                    type="color"
                    value={localOosColor || '#555555'}
                    onChange={(e) => setLocalOosColor(e.target.value)}
                    onBlur={() =>
                      handleSaveOosField('oos_button_color', localOosColor.trim())
                    }
                    className="absolute inset-0 size-8 cursor-pointer opacity-0"
                    disabled={isUpdating}
                    aria-label="Custom button color"
                  />
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border"
                    style={{ backgroundColor: localOosColor || '#555555' }}
                  >
                    <svg className="size-3.5 text-white drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <Divider />

        {/* Enable B2B Quotation Mode */}
        <SettingRow
          label="B2B Quotation Mode"
          subtitle="Allow customers to request quotations at checkout"
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.enable_b2b_quotation}
              onCheckedChange={handleToggleB2bQuotation}
              disabled={isUpdating}
              aria-label="Enable B2B quotation mode"
            />
          )}
        </SettingRow>

        <Divider />

        {/* Enable Cart Sharing */}
        <SettingRow
          label="Cart Sharing"
          subtitle="Allow customers to share their cart via a link. A 'Share this cart' button appears in the cart panel."
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.enable_cart_sharing}
              onCheckedChange={async (checked) => {
                const previousData = queryClient.getQueryData<TenantSettingsResponse>(queryKey)
                queryClient.setQueryData<TenantSettingsResponse | undefined>(
                  queryKey,
                  (old) => old ? { ...old, enable_cart_sharing: checked ? 1 : 0 } : old,
                )
                try {
                  await updateAsync({ enable_cart_sharing: checked ? 1 : 0 })
                  toast('Setting saved.')
                } catch {
                  queryClient.setQueryData(queryKey, previousData)
                  toast.error('Failed to save. Please try again.')
                }
              }}
              disabled={isUpdating}
              aria-label="Enable cart sharing"
            />
          )}
        </SettingRow>

        <Divider />

        {/* Disable Image Right-Click */}
        <SettingRow
          label="Disable Image Right-Click"
          subtitle="Prevent customers from right-clicking to save images. Text remains copyable."
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.disable_image_right_click}
              onCheckedChange={async (checked) => {
                const previousData = queryClient.getQueryData<TenantSettingsResponse>(queryKey)
                queryClient.setQueryData<TenantSettingsResponse | undefined>(
                  queryKey,
                  (old) => old ? { ...old, disable_image_right_click: checked ? 1 : 0 } : old,
                )
                try {
                  await updateAsync({ disable_image_right_click: checked ? 1 : 0 })
                  toast('Setting saved.')
                } catch {
                  queryClient.setQueryData(queryKey, previousData)
                  toast.error('Failed to save. Please try again.')
                }
              }}
              disabled={isUpdating}
              aria-label="Disable image right-click on storefront"
            />
          )}
        </SettingRow>
      </CardContent>
    </Card>
  )
}
